import { Clear, DefaultProfilePicture, House, Identity } from 'assets';
import { GeoAreaSelection, State, StateView } from 'components';
import {
  useCreatePerson,
  useDeleteTemporaryMediaItem,
  useGetPerson,
  useUpdatePerson,
  useUploadTemporaryMediaItem
} from 'hooks';
import { useAuth } from 'providers';
import { FC, useCallback, useEffect, useState } from 'react';
import { Oval } from 'react-loader-spinner';
import { getValue, isEmptyOrNull } from 'utils';

import './AccountCreateEdit.scss';

const AppVariantIdentifier = 'de.fhg.iese.slr.mein-digitaler-landkreis';

export const AccountCreateEdit: FC = () => {
  const { authProfile, isAuthenticated, login } = useAuth();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const { data: person, isLoading: isPersonLoading } = useGetPerson({});
  const { mutate: createPerson } = useCreatePerson();
  const { mutate: updatePerson } = useUpdatePerson();

  const [errors, setErrors] = useState<string[]>();

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [email, setEmail] = useState<string>(authProfile?.email ?? '');
  const [homeAreaId, setHomeAreaId] = useState<string>('');
  const [profilePictureId, setProfilePictureId] = useState<
    string | undefined
  >();
  const [profilePictureURL, setProfilePictureURL] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (!isAuthenticated) {
      login();
    }
  }, [isAuthenticated, login]);

  useEffect(() => {
    if (isAuthenticated) {
      setIsEditMode(person !== undefined);
      setFirstName(person?.firstName ?? '');
      setLastName(person?.lastName ?? '');
      setEmail(person?.email ?? '');
      setHomeAreaId(person?.homeAreaId ?? '');
      setProfilePictureId(person?.profilePicture?.id);
      setProfilePictureURL(
        person?.profilePicture
          ? getValue('original', person.profilePicture.urls)
          : undefined
      );
    }
  }, [isAuthenticated, person]);

  // #region Media Item

  const [temporaryMediaItemId, setTemporaryMediaItemId] = useState<
    string | undefined
  >();

  const { mutate: uploadTemporaryMediaItem, isPending: isMediaUploadLoading } =
    useUploadTemporaryMediaItem();
  const { mutate: deleteTemporaryMediaItem, isPending: isMediaDeleteLoading } =
    useDeleteTemporaryMediaItem();

  /**
   * Handle image selection
   * @param event HTMLInputElement
   */
  const handleProfilePictureChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = (event.target as HTMLInputElement).files;
    if (files) {
      const fileSize = files[0].size / 1000000;

      if (fileSize < 5 && files[0] !== undefined) {
        const file = files[0];

        setErrors([]);
        uploadTemporaryMediaItem(
          {
            file
          },
          {
            onError: () => {
              setErrors(['Das Bild konnte nicht hochgeladen werden.']);
            },
            onSuccess: (image) => {
              // Delete old temporary media item
              if (
                temporaryMediaItemId &&
                !isEmptyOrNull(temporaryMediaItemId)
              ) {
                deleteTemporaryMediaItem({
                  temporaryMediaItemId
                });
              }
              setTemporaryMediaItemId(image.id);
              setProfilePictureId(image.mediaItem?.id);
              setProfilePictureURL(
                image?.mediaItem
                  ? getValue('original', image.mediaItem?.urls)
                  : undefined
              );
            }
          }
        );
      } else {
        setErrors(['Das Bild überschreitet das Upload-Limit von 5MB.']);
      }
    }
  };

  /**
   * Handle deleting image
   * @param event HTMLInputElement
   */
  const handleProfilePictureDelete = () => {
    setProfilePictureURL(undefined);
    setProfilePictureId(undefined);
    setTemporaryMediaItemId(undefined);
    // Delete if temporary media item
    if (temporaryMediaItemId && !isEmptyOrNull(temporaryMediaItemId)) {
      deleteTemporaryMediaItem({
        temporaryMediaItemId
      });
    }
    setErrors([]);
  };

  // #endregion Media Item

  const [canSubmit, setCanSubmit] = useState(false);

  useEffect(() => {
    setCanSubmit(
      !isEmptyOrNull(firstName) &&
        !isEmptyOrNull(lastName) &&
        !isEmptyOrNull(email) &&
        !isEmptyOrNull(homeAreaId) &&
        (person?.firstName !== firstName.trim() ||
          person?.lastName !== lastName.trim() ||
          person?.email !== email?.trim() ||
          person?.homeAreaId !== homeAreaId ||
          person?.profilePicture?.id !== profilePictureId)
    );
  }, [
    email,
    firstName,
    homeAreaId,
    lastName,
    person,
    profilePictureId,
    temporaryMediaItemId
  ]);

  const handleSubmit = useCallback(() => {
    if (canSubmit) {
      if (isEditMode && person?.id) {
        updatePerson(
          {
            clientPersonExtendedUpdateRequest: {
              firstName,
              lastName,
              homeAreaId,
              profilePicture: temporaryMediaItemId
                ? {
                    temporaryMediaItemId
                  }
                : undefined
            }
          },
          {
            onSuccess: () => {
              setTemporaryMediaItemId(undefined);
              console.warn('todo');
            }
          }
        );
      } else {
        createPerson(
          {
            clientPersonCreateRequest: {
              email,
              firstName,
              lastName,
              homeAreaId
            }
          },
          {
            onSuccess: () => {
              console.warn('todo');
            }
          }
        );
      }
    }
  }, [
    canSubmit,
    createPerson,
    email,
    firstName,
    homeAreaId,
    isEditMode,
    lastName,
    person?.id,
    temporaryMediaItemId,
    updatePerson
  ]);

  return (
    <div className="create-edit">
      {errors?.map((errorMessage, index) => (
        <p className="error" key={index}>
          {errorMessage} <br />
        </p>
      ))}
      {isPersonLoading && <StateView state={State.Loading} />}
      {isAuthenticated && !isPersonLoading && (
        <>
          <p className="title">
            {isEditMode
              ? 'Dein Profil'
              : 'Nur noch wenige Angaben, um deine Registrierung abzuschließen:'}
          </p>
          {isEditMode && (
            <div className="profile-picture ">
              <label htmlFor="picture">
                <input
                  type="file"
                  name="avatar"
                  accept="image/png, image/jpeg"
                  id="picture"
                  onChange={handleProfilePictureChange}
                  hidden
                />
                {isMediaUploadLoading ? (
                  <Oval
                    color="#b1dae7"
                    wrapperClass="image"
                    visible={true}
                    ariaLabel="loading"
                    secondaryColor="#b1dae7"
                    strokeWidth={2}
                    strokeWidthSecondary={2}
                  />
                ) : (
                  <img
                    className="image"
                    alt="Profile-Picture"
                    src={profilePictureURL ?? DefaultProfilePicture}
                  />
                )}
              </label>
              {profilePictureId &&
                !(isMediaUploadLoading || isMediaDeleteLoading) && (
                  <button
                    className="delete-icon"
                    onClick={handleProfilePictureDelete}
                  >
                    <img alt="Löschen" src={Clear} />
                  </button>
                )}
            </div>
          )}
          <form className="name-input">
            <img alt="Vorname" src={Identity} className="icon" />
            <input
              name="firstName"
              type="text"
              placeholder="Vorname"
              value={firstName}
              onChange={(ev) => {
                setFirstName(ev.target.value);
              }}
            />
          </form>
          <form className="name-input ">
            <img alt="Nachname" src={Identity} className="icon" />
            <input
              name="lastName"
              type="text"
              placeholder="Nachname"
              value={lastName}
              onChange={(ev) => {
                setLastName(ev.target.value);
              }}
            />
          </form>
          {isEditMode && (
            <form className="name-input ">
              <img alt="E-Mail" src={Identity} className="icon" />
              <input
                name="email"
                type="text"
                placeholder="E-Mail"
                disabled
                value={email}
              />
            </form>
          )}
          <div className="geo-area">
            <img alt="Gemeinde" src={House} className="icon" />
            <GeoAreaSelection
              selectedGeoAreaId={homeAreaId}
              appVariantIdentifier={AppVariantIdentifier}
              onChange={setHomeAreaId}
            />
          </div>
          <button
            className="submit-button"
            type="submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isEditMode ? 'Übernehmen' : 'Fertig'}
          </button>
        </>
      )}
    </div>
  );
};
